import { HchbCoordinationNotesServiceImplFactory } from "../../core/impl/hchbCoordinationNotesServiceImpl";
import type { HchbCoordinationNoteSource } from "../driven/repositories/hchbCoordinationNotesRepository";

export const HchbCoordinationNotesControllerFactory = ({
  hchbCoordinationNotesService,
}: {
  hchbCoordinationNotesService: ReturnType<
    typeof HchbCoordinationNotesServiceImplFactory
  >;
}) => {
  const getNoteTypes = async ({
    clinicId,
    patientId,
  }: {
    clinicId: number;
    patientId: number;
  }) => {
    try {
      const result = await hchbCoordinationNotesService.getNoteTypes({
        clinicId,
        patientId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      return {
        success: false as const,
        error: !result.success ? result.error : { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const listNotes = async ({
    clinicId,
    patientId,
    limit,
    offset,
    source,
  }: {
    clinicId: number;
    patientId: number;
    limit?: number;
    offset?: number;
    source?: HchbCoordinationNoteSource | null;
  }) => {
    try {
      const result = await hchbCoordinationNotesService.listNotes({
        clinicId,
        patientId,
        limit,
        offset,
        source,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      return {
        success: false as const,
        error: !result.success ? result.error : { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const createNote = async ({
    clinicId,
    patientId,
    hchbNoteType,
    hchbNoteTypeDescription,
    noteMessage,
    source,
  }: {
    clinicId: number;
    patientId: number;
    hchbNoteType: string;
    hchbNoteTypeDescription?: string;
    noteMessage: string;
    source?: HchbCoordinationNoteSource | null;
  }) => {
    try {
      const result = await hchbCoordinationNotesService.createNote({
        clinicId,
        patientId,
        hchbNoteType,
        hchbNoteTypeDescription,
        noteMessage,
        source,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      return {
        success: false as const,
        error: !result.success ? result.error : { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const retryNote = async ({
    clinicId,
    patientId,
    coordinationNoteId,
  }: {
    clinicId: number;
    patientId: number;
    coordinationNoteId: number;
  }) => {
    try {
      const result = await hchbCoordinationNotesService.retryNote({
        clinicId,
        patientId,
        coordinationNoteId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      return {
        success: false as const,
        error: !result.success ? result.error : { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  return { getNoteTypes, listNotes, createNote, retryNote };
};
